import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { FooterComponent } from '../../../../shared/components/footer/footer.component';
import { AssetHeaderComponent, AssetHeaderConfig } from '../../../../shared/components/asset-header/asset-header.component';
import { DiscussionSidebarComponent } from '../../discussion-sidebar/discussion-sidebar.component';
import { DiscussionListComponent, DiscussionItem } from '../../shared/discussion-list/discussion-list.component';
import { DiscussionApiService } from '@angular-workspace/services';
import { KeycloakService } from 'keycloak-angular';
import { LoginModalComponent } from '../../../../shared/components/login-modal/login-modal.component';
import { Router } from '@angular/router';
import { ToasterService } from '../../../../services/toaster.service';

@Component({
  selector: 'app-discussion-my-bookmarks',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    FooterComponent,
    AssetHeaderComponent,
    DiscussionSidebarComponent,
    DiscussionListComponent,
    LoginModalComponent
  ],
  templateUrl: './my-bookmarks.component.html',
  styleUrl: './my-bookmarks.component.scss',
})
export class DiscussionMyBookmarksComponent implements OnInit {
  private router = inject(Router);
  private discussionApiService = inject(DiscussionApiService);
  private keycloakService = inject(KeycloakService);
  private toasterService = inject(ToasterService);

  discussions: DiscussionItem[] = [];
  isLoggedIn: boolean = false;
  showLoginModal: boolean = false;
  activeTab: string = 'my-bookmarks';

  headerConfig: AssetHeaderConfig = {
    breadcrumbs: [
      { label: 'Home', route: '/' },
      { label: 'Discussions', route: '/discussions' },
      { label: 'My Bookmarks' }
    ],
    title: 'My Bookmarks',
    description: 'View your bookmarked discussions'
  };

  ngOnInit(): void {
    this.checkLoginStatus();
    this.fetchBookmarkedDiscussions();
  }

  async checkLoginStatus(): Promise<void> {
    try {
      this.isLoggedIn = await this.keycloakService.isLoggedIn();
    } catch (error) {
      console.error('Error checking login status:', error);
      this.isLoggedIn = false;
    }
  }

  async fetchBookmarkedDiscussions(): Promise<void> {
    try {
      // Fetch bookmarked discussions using the dedicated API endpoint
      // Filters as per curl: {"type":null,"status":null,"tags":null}
      const response = await this.discussionApiService.getBookmarkedDiscussions({
        filters: {
          type: null,
          status: null,
          tags: null
        },
        sort_by: 'newest',
        page: 1,
        limit: 10,
        pinned: true
      });

      // Transform API response to DiscussionItem format
      const list = this.getDiscussionsListFromResponse(response);
      this.discussions = list.map((item: any) => this.transformToDiscussionItem(item));
    } catch (error) {
      console.error('Error fetching bookmarked discussions:', error);
      this.discussions = [];
    }
  }

  /** Extract discussions array from API response (handles data.discussions, data, etc.) */
  private getDiscussionsListFromResponse(response: any): any[] {
    if (!response) return [];
    const data = response.data ?? response;
    if (Array.isArray(data)) return data;
    if (data?.discussions && Array.isArray(data.discussions)) return data.discussions;
    if (data?.discussion && Array.isArray(data.discussion)) return data.discussion;
    return [];
  }

  transformToDiscussionItem(item: any): DiscussionItem {
    return {
      id: item.id || item._id || '',
      title: item.title || 'Untitled Discussion',
      description: item.description || item.content || '',
      author: item.user?.name || item.author?.name || item.author || item.created_by || 'Unknown',
      category: item.category ?? '',
      discussionType:
        [item.type, item.discussion_type]
          .map((v) => (v != null && String(v).trim() ? String(v).trim() : ''))
          .find((s) => s) || undefined,
      replies: item.reply_count ?? item.replies ?? item.comment_count ?? 0,
      views: item.view_count ?? item.views ?? 0,
      lastActivity: item.updated_at ?? item.updatedAt ?? item.last_activity ?? item.created_at ?? item.createdAt ?? new Date().toISOString(),
      isPinned: item.is_pinned ?? item.pinned ?? false,
      isBookmarked: item.is_bookmarked ?? item.bookmarked ?? false,
      tags: item.tags || [],
      attachments: item.attachments || []
    };
  }

  createNewDiscussion(): void {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }
    this.router.navigate(['/discussions/create-new']);
  }

  async onBookmarkClick(event: { event: Event; discussion: DiscussionItem }): Promise<void> {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }

    const newBookmarkState = !event.discussion.isBookmarked;
    const previousState = event.discussion.isBookmarked;
    event.discussion.isBookmarked = newBookmarkState;
    try {
      if (previousState) {
        await this.discussionApiService.unbookmarkDiscussion(event.discussion.id);
      } else {
        await this.discussionApiService.bookmarkDiscussion(event.discussion.id);
      }
      this.toasterService.success(
        newBookmarkState
          ? 'This discussion has been added to your bookmarks. You can open it anytime from My Bookmarks.'
          : 'This discussion has been removed from your bookmarks.'
      );
      if (!newBookmarkState) {
        this.discussions = this.discussions.filter(d => d.id !== event.discussion.id);
      }
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      event.discussion.isBookmarked = previousState;
      this.toasterService.error((error as any)?.error?.detail || 'Failed to update bookmark');
    }
  }

  async onPinClick(event: { event: Event; discussion: DiscussionItem }): Promise<void> {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }

    const newPinState = !event.discussion.isPinned;
    const previousState = event.discussion.isPinned;
    event.discussion.isPinned = newPinState;
    try {
      if (previousState) {
        await this.discussionApiService.unpinDiscussion(event.discussion.id);
      } else {
        await this.discussionApiService.pinDiscussion(event.discussion.id);
      }
      this.toasterService.success(
        newPinState
          ? 'This discussion is now pinned. Find it under My Pinned in Discussions.'
          : 'This discussion has been unpinned.'
      );
    } catch (error) {
      console.error('Error toggling pin:', error);
      event.discussion.isPinned = previousState;
      this.toasterService.error((error as any)?.error?.detail || 'Failed to update pin');
    }
  }
}
